'use client';

import { useLocation, Link } from 'react-router-dom';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MasterAccountService } from '@/core/services/masterAccountService';
import { ExchangeService } from '@/core/services/exchangeService';
import { DcaService } from '@/core/services/dcaService';
import { MandateService } from '@/core/services/mandateService';
import { WalletService } from '@/core/services/walletService';

interface MenuItem {
  link: string;
  name?: string;
  icon?: React.ReactNode;
  status?: string;
  isDisabled?: boolean; // Add this to control whether the link is active
}

export default function MenuLink({ item, onClick }: { item: MenuItem, onClick?: () => void }) {
  const location = useLocation();
  const pathname = location.pathname;
  const isActive = item.link === pathname;
  const className = `menu-link-item ${isActive ? 'active' : ''}`;
  const queryClient = useQueryClient();

  // Prefetch data for common routes on hover
  const handlePrefetch = () => {
    if (item.link === '/main-account') {
      queryClient.prefetchQuery({
        queryKey: ['mainAccountOverview'],
        queryFn: () => MasterAccountService.getMainAccountOverview(),
      });
      queryClient.prefetchQuery({
        queryKey: ['mainAccountAllocations'],
        queryFn: () => MasterAccountService.getMainAccountAllocation(),
      });
    } else if (item.link === '/exchange') {
      queryClient.prefetchQuery({
        queryKey: ['exchangeOverview'],
        queryFn: () => ExchangeService.getExchangeOverview(),
      });
      queryClient.prefetchQuery({
        queryKey: ['exchangeAllocations'],
        queryFn: () => ExchangeService.getExchangeAllocations(),
      });
    } else if (item.link === '/dca-strategy') {
      queryClient.prefetchQuery({
        queryKey: ['dcaStrategies'],
        queryFn: () => DcaService.getAllDCAModel(),
      });
    } else if (item.link === '/management-mandate') {
      queryClient.prefetchQuery({
        queryKey: ['listMandate'],
        queryFn: () => MandateService.getListMandate(),
      });
    } else if (item.link === '/') {
      queryClient.prefetchQuery({
        queryKey: ['walletOverview'],
        queryFn: () => WalletService.getWalletOverview(),
      });
    }
  };

  return item.isDisabled ? (
    <div className={className} onClick={onClick}>
      {item.icon && <div className="menu-link-icon">{item.icon}</div>}
      {item.name && <span className="menu-link-name">{item.name}</span>}
      {item.status && <span className="menu-link-status">{item.status}</span>}
    </div>
  ) : (
    <Link to={item.link} className={className} onMouseEnter={handlePrefetch}>
      {item.icon && <div className="menu-link-icon">{item.icon}</div>}
      {item.name && <span className="menu-link-name">{item.name}</span>}
      {item.status && <span className="menu-link-status">{item.status}</span>}
    </Link>
  );
}
